import { useMsal } from "@azure/msal-react";
import { Paper, Typography, Button, FormControl, RadioGroup, FormLabel, FormControlLabel, Radio } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { de } from "date-fns/locale";
import React, { ReactElement, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { globalStyles } from "../../assets/globalStyles";
import { AutocompleteMesswerte } from "./autocompleteMesswerte";
import { useStyles } from "./datenvollstaendigkeitStyles";
import { addTimezoneOffset, subtractTimezoneOffset, toUTCDateString } from '../../utils/utils';
import DateFnsUtils from "@date-io/date-fns";
import { selectDate } from "../../features/konfiguration/konfigurationSlice";
import { getMessstellenForLookup, selectMessstellenDictionary } from "../../features/messstellenLookup/messstellenLookupSlice";
import {
    setTempEndedatum, setTempStartdatum, selectTempEndeDatum, selectTempStartDatum,
    selectErsteDatum, selectLetzteDatum, getGraph, setAktuellerWert, selectTempAktuellerWert, setShowGraph, selectShowDatePicker, setShowDatePicker,
    selectMessstellenLoading, getMessstellen, setSelectedZeitraum, selectSelectedZeitraum
} from "./datenvollstaendigkeitSlice";

export default function MessstellenAuswahlGrid(): ReactElement {
    const dispatch = useAppDispatch();
    const { instance } = useMsal();

    useEffect(() => {
        dispatch(getMessstellen(isUTC));
        dispatch(getMessstellenForLookup());
    }, [instance]);

    const classes = {
        ...useStyles(),
        ...globalStyles(),
    };
    const {
        datePicker,
        showGraphButton
    } = useStyles();

    const [openGroups, setOpenGroups] = React.useState<Array<string>>([]);
    const [selectedGroups, setSelectedGroups] = React.useState<Array<string>>([]);
    const isUTC = useAppSelector(selectDate);
    const ersteDatum = useAppSelector(selectErsteDatum);
    const letzteDatum = useAppSelector(selectLetzteDatum);
    const tempAktuellerWert = useAppSelector(selectTempAktuellerWert);
    const eigeneZeitAngabe = useAppSelector(selectShowDatePicker);
    const tempStartDatum = useAppSelector(selectTempStartDatum);
    const tempEndeDatum = useAppSelector(selectTempEndeDatum);
    const messstellenLoading = useAppSelector(selectMessstellenLoading);
    const messstellenDict = useAppSelector(selectMessstellenDictionary);
    const selectedZeitraum= useAppSelector(selectSelectedZeitraum);


    function setZeitraum(zeitraum: string) {
        dispatch(setSelectedZeitraum(zeitraum));
        const from = new Date();
        const now = new Date();
        switch (zeitraum) {
            case "manuel":
                dispatch(setShowDatePicker(true));
                break;
            case "30T":
                from.setMonth(now.getMonth() - 1)
                dispatch(setTempStartdatum(isUTC ? subtractTimezoneOffset(from) : from));
                dispatch(setTempEndedatum(isUTC ? subtractTimezoneOffset(now) : now));
                dispatch(setShowDatePicker(false));
                break;
            case "6M":
                from.setMonth(now.getMonth() - 6)
                dispatch(setShowDatePicker(false));
                dispatch(setTempStartdatum(isUTC ? subtractTimezoneOffset(from) : from));
                dispatch(setTempEndedatum(isUTC ? subtractTimezoneOffset(now) : now));
                break;
            case "12M":
                from.setFullYear(now.getFullYear() - 1)
                dispatch(setShowDatePicker(false));
                dispatch(setTempStartdatum(isUTC ? subtractTimezoneOffset(from) : from));
                dispatch(setTempEndedatum(isUTC ? subtractTimezoneOffset(now) : now));
                break;
            case "gesamt":
                dispatch(setShowDatePicker(false));
                dispatch(setTempStartdatum(isUTC ? subtractTimezoneOffset(ersteDatum) : ersteDatum));
                dispatch(setTempEndedatum(isUTC ? subtractTimezoneOffset(letzteDatum) : letzteDatum));
                break;
            default:
                dispatch(setShowDatePicker(false));
                break;
        }
    }

    return (
        <React.Fragment >
            <Paper className={classes.paper} >
                <form className={classes.root} noValidate autoComplete="off" style={{ marginLeft: "16px", marginRight: "16px", marginBottom: "10px" }}>
                    <div>
                        <Grid container>
                            <Grid container spacing={4}>
                                <Grid item container xs={12}>
                                    <Grid item md={3}>
                                        <Typography color="primary" variant="h6" className={classes.typoH6Style} component={"div"}>
                                            Messstellen Auswahl
                                        </Typography>
                                    </Grid>
                                    <AutocompleteMesswerte
                                        openGroups={openGroups}
                                        selectedGroups={selectedGroups}
                                        setOpenGroups={setOpenGroups}
                                        setSelectedGroups={setSelectedGroups} />
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl component="fieldset" className={classes.fullWidth} style={{ marginTop: "26px" }}>
                                                <FormLabel component="legend" color="secondary" style={{ textAlign: "left", marginBottom: "0px", }} className={classes.sectionLabelControl}>Zeitraum</FormLabel>
                                                <RadioGroup row value={selectedZeitraum} onChange={(event) => setZeitraum(event.target.value as string)}>
                                                    <FormControlLabel value="30T" control={<Radio size="small" />} label="Letzte 30 Tage" className={classes.RadioButtonLabel} />
                                                    <FormControlLabel value="6M" control={<Radio size="small" />} label="Letzte 6 Monate" className={classes.RadioButtonLabel} />
                                                    <FormControlLabel value="12M" control={<Radio size="small" />} label="Letzte 12 Monate" className={classes.RadioButtonLabel} />
                                                    <FormControlLabel value="gesamt" control={<Radio size="small" />} label="Gesamte Zeit" className={classes.RadioButtonLabel} />
                                                    <FormControlLabel value="manuel" control={<Radio size="small" />} label="Eigene Angabe" className={classes.RadioButtonLabel} />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={4}>
                                        {eigeneZeitAngabe ?
                                            <React.Fragment>
                                                <Grid item xs={12} md={6}>
                                                    <MuiPickersUtilsProvider
                                                        locale={de}
                                                        utils={DateFnsUtils}>
                                                        <KeyboardDateTimePicker
                                                            value={isUTC ? addTimezoneOffset(tempStartDatum) : new Date(tempStartDatum)}
                                                            className={datePicker}
                                                            format="dd.MM.yyyy HH:mm:ss"
                                                            label="Startzeitpunkt"
                                                            okLabel="OK"
                                                            cancelLabel="ABBRECHEN"
                                                            ampm={false}
                                                            disableFuture={true}
                                                            invalidDateMessage={"Das ausgewählte Datum hat ein ungültiges Format."}
                                                            inputVariant="outlined"
                                                            onChange={(event) => {
                                                                dispatch(
                                                                    setTempStartdatum(isUTC ? subtractTimezoneOffset(new Date(event + "")) : new Date(event + ""))
                                                                );
                                                            }}
                                                        //disabled={new Date(ersteDatum).getTime() === new Date(0).getTime()}
                                                        ></KeyboardDateTimePicker>
                                                    </MuiPickersUtilsProvider>
                                                    <Typography variant="caption">
                                                        {new Date(ersteDatum).getTime() == new Date(0).getTime()
                                                            ? ""
                                                            : "Erste Daten: " + (isUTC ? toUTCDateString(new Date(ersteDatum)) : new Date(ersteDatum).toLocaleString())}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MuiPickersUtilsProvider locale={de} utils={DateFnsUtils}>
                                                        <KeyboardDateTimePicker
                                                            value={isUTC ? addTimezoneOffset(tempEndeDatum) : new Date(tempEndeDatum)}
                                                            className={datePicker}
                                                            format="dd.MM.yyyy HH:mm:ss"
                                                            label="Endzeitpunkt"
                                                            okLabel="OK"
                                                            cancelLabel="ABBRECHEN"
                                                            ampm={false}
                                                            disableFuture={true}
                                                            invalidDateMessage={"Das ausgewählte Datum hat ein ungültiges Format."}
                                                            inputVariant="outlined"
                                                            onChange={(event) => {
                                                                dispatch(
                                                                    setTempEndedatum(isUTC ? subtractTimezoneOffset(new Date(event + "")) : new Date(event + ""))
                                                                );
                                                            }}
                                                        //disabled={new Date(letzteDatum).getTime() == new Date(0).getTime()}
                                                        ></KeyboardDateTimePicker>
                                                    </MuiPickersUtilsProvider>
                                                    <Typography variant="caption">
                                                        {new Date(letzteDatum).getTime() === new Date(0).getTime()
                                                            ? ""
                                                            : "Letzte Daten: " +
                                                            (isUTC ? toUTCDateString(new Date(letzteDatum)) : new Date(letzteDatum).toLocaleString())}
                                                    </Typography>
                                                </Grid>
                                            </React.Fragment>
                                            : ""}
                                        <Grid item xs={12} md={3}>
                                            <Grid container item >
                                                <Grid item md style={{ display: "grid", marginTop: "20px" }}>

                                                    <Button
                                                        variant="contained"
                                                        onClick={() => {
                                                            dispatch(setShowGraph(true));
                                                            dispatch(setAktuellerWert(tempAktuellerWert));
                                                            dispatch(getGraph(tempAktuellerWert, tempStartDatum, tempEndeDatum, messstellenDict));
                                                        }}
                                                        color="secondary"
                                                        className={showGraphButton}
                                                        disabled={!messstellenLoading ? tempAktuellerWert.length == 0 : false}
                                                    >
                                                        Graph zeigen
                                                    </Button>

                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </div>
                </form>
            </Paper>
        </React.Fragment>
    );
}